import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Checkbox, InputAdornment, TextField, Button, CircularProgress } from '@mui/material';
import { TableWrapper, TableRowStyled } from './UserListTable.styles'; // Imported styles
import { useParams } from 'react-router-dom';
import AvatarImage from '../../../../Navbar/AvatarImage';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import Pagination from '../../../../Common/Pagination/pagination';
import { theme } from '../../../../../theme';
import { getAllUsers } from '../../User/userApi';
import SearchBar from '../../../../Common/Search/SearchBar';
import { getAllUserNums, sendDashboardMessages, sendMultipleEmails } from '../communicationApi';
import { useSnackbar } from '../../../../../Context/SnackbarContext';
import { useLoadingBackdrop } from '../../../../../Context/LoadingBackdropContext';

interface userDataType {
  UserNum: number;
  UserName: string;
  EmailID: string;
  UserID: string;
  DeleteItem: boolean;
  UpdatedBy: string;
  UserRole: string;
  UserStatus: string;
  ImageData: string;
  MIMEType: string
}

interface UserListProps {
  selectedEmailId: number | null
  handleClearAll: () => void;
  tabName: string
}

const UserList: React.FC<UserListProps> = ({ selectedEmailId, handleClearAll, tabName }) => {
  const [page, setPage] = useState(1); // Adjusted to start at page 1
  const [rowsPerPage, setRowsPerPage] = useState(10); // Default rows per page
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]); // Store selected user IDs
  const [totalExpectedUserIds, setTotalExpectedUserIds] = useState<number[]>([]);
  const [isSelectAll, setIsSelectAll] = useState(false); // Control Select All checkbox
  const [searchQuery, setSearchQuery] = useState(''); // State to track search input
  const [userData, setUserData] = useState<userDataType[]>([])
  const { tenant } = useParams<{ tenant: string }>();
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstRender = useRef(true);
  const { showSnackbar } = useSnackbar();
  const { show, hide } = useLoadingBackdrop();

  const fetchUsers = async (page: number, rowsPerPage: number, searchQuery: string) => {
    const payLoad = {
      page: page,
      pageSize: rowsPerPage,
      userName: searchQuery,
      status: 'Active'
    }
    if (searchQuery.trim()) {
      setIsSelectAll(false)
      setSelectedUsers([])
      setTotalExpectedUserIds([])
    }
    try {
      setLoading(true)
      const result = await getAllUsers(tenant, payLoad)
      if (result) {
        setUserData(result.data)
        setTotalCount(result.totalUsers)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers(page, rowsPerPage, searchQuery)
  }, [page, rowsPerPage])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return; // 🚫 Skip on initial render
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchUsers(page, rowsPerPage, searchQuery);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    setPage(1); // Reset to first page when rows per page changes
  };

  // Checkbox selection handler for individual rows
  const handleCheckboxChange = (id: number) => {
    const isAlreadySelected = selectedUsers.includes(id);
    let newSelected: number[];

    if (isAlreadySelected) {
      newSelected = selectedUsers.filter(loginId => loginId !== id);

      // ✅ If deselecting a user and "Select All" was true, turn it off
      if (isSelectAll) {
        setIsSelectAll(false);
      }
    } else {
      newSelected = [...selectedUsers, id];
    }
    setSelectedUsers(newSelected)

    // Optional: recheck if all are selected and restore selectAll
    // You'd need to track `totalExpectedUserIds.length`
    if (!isAlreadySelected && totalExpectedUserIds.length > 0) {
      const selectedSet = new Set(newSelected);
      const allSelected = totalExpectedUserIds.every(id => selectedSet.has(id));
      if (allSelected) {
        setIsSelectAll(true);
      }
    }

  };

  // Handle Select All checkbox
  const handleSelectAll = async () => {
    if (isSelectAll) {
      setSelectedUsers([]); // Deselect all
      setTotalExpectedUserIds([])
    } else {
      const payLoad = {
        userName: searchQuery,
        page: page,
        pageSize: rowsPerPage
      }
      const result = await getAllUserNums(tenant, payLoad)
      setSelectedUsers(result); // Select all visible users
      setTotalExpectedUserIds(result)
    }
    setIsSelectAll(!isSelectAll);
  };

  // Check if all current page rows are selected

  const sendEmailsToUsers = async () => {

    try {
      if (!selectedUsers.length) {
        showSnackbar("Please select at least one user to send the email.", "error");
        return
      }
      show()
      const payLoad = {
        EmailNum: selectedEmailId,
        Users: selectedUsers
      }
      const response = await sendMultipleEmails(tenant, payLoad)
      if (response.Status) {
        showSnackbar(response.Message, "success")
        setIsSelectAll(false)
        setSelectedUsers([])
        setTotalExpectedUserIds([])
        handleClearAll()
      } else {
        showSnackbar(response.Message, "error")
      }
    } catch (error) {
      console.log(error)
    } finally {
      hide()
    }

  }

  const sendMessagesToUsers = async () => {
    try {
      if (!selectedUsers.length) {
        showSnackbar("Please select at least one user to send the email.", "error");
        return
      }
      show()
      const payLoad = {
        MessageNum: selectedEmailId,
        Users: selectedUsers
      }

      const response = await sendDashboardMessages(tenant, payLoad)
      if (response.Status) {
        showSnackbar(response.Message, "success")
        setIsSelectAll(false)
        setSelectedUsers([])
        setTotalExpectedUserIds([])
        handleClearAll()
      } else {
        showSnackbar(response.Message, "error")
      }
    } catch (error) {
      console.log(error)
    } finally {
      hide()
    }

  }

  const handleSend = () => {
    if (tabName === 'Email') {
      sendEmailsToUsers()
    } else {
      sendMessagesToUsers()
    }
  }


  return (
    <Box mt={4} sx={{ maxWidth: '100%', margin: '5vh 0', boxSizing: 'border-box' }}>
      {/* Search Box and Send Message Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '1.6vw' }}>
        <SearchBar
          placeholder='Search by Username'
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />
        <Button size='small' sx={{ p: '0 2vw' }} variant="contained" onClick={handleSend}>
          {tabName === 'Email' ? "Send Email" : "Send Message"}
        </Button>
      </Box>

      <TableWrapper sx={{ border: '0.0694vw  solid #E4E7EC', borderRadius: '1vw', boxShadow: 0 }}>
        <TableContainer component={Paper} sx={{ boxShadow: 0, border: '0.0694vw  solid #E4E7EC', borderRadius: '0' }} elevation={0}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '6vw', textAlign: 'center', p: '0.8vw' }}>
                  <Checkbox
                    checked={selectedUsers.length > 0 && isSelectAll}
                    onChange={handleSelectAll}
                  />
                </TableCell>

                <TableCell sx={{ width: '14vw', p: '0.8vw' }}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}>
                    User ID
                  </Typography>
                </TableCell>

                <TableCell sx={{ width: '22vw', p: '0.8vw' }}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}>
                    Username
                  </Typography>
                </TableCell>

                <TableCell sx={{ width: '28vw', p: '0.8vw' }}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}>
                    Email Address
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} sx={{ textAlign: 'center', py: '2vw' }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : userData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} sx={{ textAlign: 'center', fontWeight: 'bold', py: '2vw', color: theme.palette.text.secondary }}>
                    No User Data Available
                  </TableCell>
                </TableRow>
              ) : (
                userData.map((row, index) => (
                  <TableRowStyled
                    key={row.UserNum}
                    isEven={index % 2 === 0}
                    sx={{ width: '100%' }}
                  >
                    <TableCell sx={{ textAlign: 'center', width: '6vw', p: '0.8vw' }}>
                      <Checkbox
                        checked={selectedUsers.includes(row.UserNum)}
                        onChange={() => handleCheckboxChange(row.UserNum)}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '14vw', p: '0.8vw', whiteSpace: 'nowrap' }}>
                      <Typography variant="body2">{row.UserID}</Typography>
                    </TableCell>

                    <TableCell sx={{ width: '22vw', p: '0.8vw' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.6vw' }}>
                        {row.ImageData ? (
                          <AvatarImage imageData={row.ImageData} Mimetype={row.MIMEType} />
                        ) : (
                          <AccountCircleOutlinedIcon sx={{ fontSize: '2.8vw' }} />
                        )}
                        <Typography variant="body2" noWrap>
                          {row.UserName}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ width: '28vw', p: '0.8vw', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <Typography variant="body2">{row.EmailID}</Typography>
                    </TableCell>
                  </TableRowStyled>
                ))
              )}
            </TableBody>
          </Table>

        </TableContainer>

        {/* Pagination component */}
        {totalCount > 0 && (
          <Box sx={{ mt: '0.83vw', display: 'flex', justifyContent: 'center', borderRadius: '1vw', pb: '0.56vw' }}>
            <Pagination
              totalRows={totalCount}
              rowsPerPage={rowsPerPage}
              currentPage={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        )}

      </TableWrapper>
    </Box>
  );
};

export default UserList;
